import { css, html, LitElement } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { checkIcon, chevronDownIcon, chevronUpIcon, clipboardIcon, downloadIcon } from "@blissary/shell-components/icons";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { repeat } from "lit/directives/repeat.js";
import { buildBlissSvg, buildMiniBlissSvg } from "./bliss-svg-utils.js";
import "@awesome.me/webawesome/dist/components/card/card.js";

class BlResultItem extends LitElement {
  static get properties() {
    return {
      item: { type: Object },
      colorScheme: { type: String },
      expanded: { type: Boolean },
      exportFormat: { type: String },
      searchString: { type: String },
    };
  }

  constructor() {
    super();
    this.expanded = false;
  }

  static get styles() {
    return css`
      :host {
        display: block;
      }

      wa-card {
        --spacing: 0;
        --wa-color-surface-default: var(--bl-color-surface-elevated, #ffffff);
        --wa-shadow-s: var(--bl-shadow-sm);
      }

      wa-card.non-preferred {
        --wa-color-surface-border: var(--bl-color-border-non-preferred, #bc8f8f);
        --wa-panel-border-style: dashed;
        border-width: 2px;
        background: none;
      }

      wa-card:not(.non-preferred):hover {
        --wa-color-surface-border: var(--bl-color-accent, #b89530);
      }

      /* Outer flex column */
      .cardBody {
        display: flex;
        flex-direction: column;
      }

      /* ── Top bar: badge + gloss + ID (CSS grid, expands instead of truncating) ── */
      .cardBadges {
        display: grid;
        grid-template-columns: auto 1fr auto;
        column-gap: 5px;
        row-gap: 2px;
        padding: 3px 8px;
        border-bottom: 1px solid var(--bl-color-border-card, #d5d3cf);
        align-items: baseline;
      }

      .badgeGroup {
        grid-column: 1;
        grid-row: 1;
        align-self: center;
        display: flex;
        gap: 4px;
      }

      .characterLabel,
      .nonPreferredLabel {
        font-family: var(--bl-font-body, sans-serif);
        font-size: 0.65em;
        font-weight: 600;
        padding: 1px 5px;
        border-radius: 3px;
        line-height: 1.5;
        color: white;
      }

      .characterLabel {
        background: var(--bl-color-accent, #b89530);
      }

      .nonPreferredLabel {
        background: var(--bl-color-border-non-preferred, #bc8f8f);
      }

      .dualFormLabel {
        font-family: var(--bl-font-body, sans-serif);
        font-size: 0.65em;
        font-weight: 600;
        padding: 1px 5px;
        border-radius: 3px;
        line-height: 1.5;
        color: white;
        background: var(--bl-color-border-dual-form, #7b9e89);
      }

      .resultGloss {
        grid-column: 2;
        grid-row: 1;
        min-width: 0;
        word-break: break-word;
        font-family: var(--bl-font-body, sans-serif);
        font-size: 1.1em;
        font-weight: 500;
      }

      .idChip {
        grid-column: 3;
        grid-row: 1;
        color: var(--bl-color-text-muted, #888);
        font-family: var(--bl-font-mono, monospace);
        font-size: 0.70em;
        line-height: 1.5;
        user-select: text;
        cursor: default;
        align-self: center;
      }

      .idChip span {
        user-select: all;
      }

      /* Additional language gloss — second row in top bar */
      .topBarAddLang {
        grid-column: 2;
        grid-row: 2;
        font-family: var(--bl-font-body, sans-serif);
        font-size: 1em;
        font-weight: 400;
        color: var(--bl-color-text-secondary, #555);
        min-width: 0;
        word-break: break-word;
      }

      /* ── Content: flex row — SVG + text column ───────────────────────── */
      /* No grid, so SVG height never forces empty space in the text column */
      .cardContent {
        display: flex;
        gap: 8px;
        padding: 6px 8px 6px;
        min-height: 129px;
      }

      .resultSvg {
        display: block;
        flex: 0 1 220px;
        height: 129px;
        align-self: flex-start;
        cursor: pointer;
        position: relative;
        text-decoration: none;
        color: inherit;
      }

      .resultSvg > svg {
        height: 100%;
        width: auto;
        max-width: 100%;
        border-radius: 4px;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.10), 0 1px 4px rgba(0, 0, 0, 0.08);
        transition: box-shadow 0.15s;
      }

      .resultSvg:hover > svg {
        box-shadow: 0 0 0 1px var(--bl-color-accent, #b89530), 0 2px 6px rgba(0, 0, 0, 0.12);
      }

      /* Text column: only as tall as its content */
      .cardText {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .derivationParts {
        font-size: 1em;
        min-width: 0;
      }

      .derivationParts a {
        text-decoration: none;
        color: inherit;
      }

      .explanation {
        font-family: var(--bl-font-body, sans-serif);
        padding-top: 2px;
        word-break: break-word;
        min-width: 0;
      }

      /* ── Expandable section: full-width, outside the content flex ────── */
      .expandableSection {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.3s ease;
      }

      .expandableSection.open {
        grid-template-rows: 1fr;
      }

      .expandableSection > .expandableContent {
        overflow: hidden;
      }

      .expandableContent {
        padding: 8px;
        border-top: 1px solid var(--bl-color-border-card, #d5d3cf);
        font-size: 0.9em;
        color: var(--bl-color-text-secondary, #555);
      }

      .expandableContent > div {
        margin-top: 8px;
        margin-bottom: 4px;
      }

      .icon svg, .cardActions svg {
        width: 1.2em;
        height: 1.2em;
        vertical-align: middle;
      }

      .cardActions svg {
        width: 14px;
        height: 14px;
      }

      .expandActions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 4px;
      }

      .exportLabel {
        font-size: 0.85em;
        color: var(--bl-color-text-muted, #888);
        margin-right: 2px;
      }

      .nonPreferredNotice {
        color: var(--bl-color-border-non-preferred, #bc8f8f);
        font-style: italic;
      }

      .dualFormNotice {
        color: var(--bl-color-border-dual-form, #7b9e89);
        font-style: italic;
      }

      .dualFormNotice a {
        color: inherit;
        text-decoration: underline;
      }

      /* ── Expand affordance ───────────────────────────────────────────── */
      /* Negative margin floats the chevron over the SVG's empty bottom-center.
         Takes zero net height. */
      .cardActions {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 16px;
        margin-top: -16px;
        position: relative;
        z-index: 1;
        cursor: pointer;
        opacity: 0.35;
        color: var(--bl-color-text-muted, #888);
        user-select: none;
      }

      .cardActions:hover {
        opacity: 1;
      }

      /* ── Derivation parts ────────────────────────────────────────────── */
      .derivedFromSvg {
        display: inline-block;
        height: 66px;
        width: auto;
      }

      .derivedFromSvg > svg {
        height: 100%;
        width: auto;
      }

      .derivationPart {
        display: inline-block;
        position: relative;
        padding-left: 2px;
        padding-right: 2px;
        height: 66px;
        width: auto;
        line-height: 66px;
        vertical-align: top;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-bottom: 2px solid rgba(0, 0, 0, 0.12);
        margin-top: 5px;
        border-radius: 3px;
        color: black;
      }

      .derivationPart:hover {
        border-color: var(--bl-color-accent, #b89530);
        border-bottom-color: var(--bl-color-accent, #b89530);
        background-color: rgba(0, 0, 0, 0.02);
      }

      .derivationPartArrow,
      .derivationPartPointer {
        background: rgba(0, 0, 0, 0.05);
      }

      .derivationPartText {
        text-overflow: ellipsis;
        max-width: 130px;
        height: 66px;
        overflow: hidden;
        display: inline-block;
        white-space: nowrap;
        font-size: 0.85em;
      }

      /* Search highlighting */
      .highlight {
        border-bottom: 1px solid var(--bl-color-accent-300, #ddca93);
        border-top: 1px solid var(--bl-color-accent-300, #ddca93);
        background: var(--bl-color-highlight-bg, #f0ead7);
      }

      wa-button,
      a {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        -webkit-tap-highlight-color: transparent;
      }
    `;
  }

  getMiniBlissSvg(code) {
    return buildMiniBlissSvg(code, this.colorScheme);
  }

  getBlissSvg(code, pos, options, overrideGrid) {
    return buildBlissSvg(code, pos, this.colorScheme, options, overrideGrid);
  }

  _dispatchShowMore() {
    this.dispatchEvent(
      new CustomEvent("show-more", {
        detail: { item: this.item },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _dispatchDownload() {
    this.dispatchEvent(
      new CustomEvent("download-requested", {
        detail: { item: this.item },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _toggleExpanded() {
    this.expanded = !this.expanded;
  }

  async _renderCardPngBlob() {
    const item = this.item;
    if (!item) return null;

    const cardEl = this.shadowRoot.querySelector("wa-card");
    if (!cardEl) throw new Error("no card element");

    const cardRect = cardEl.getBoundingClientRect();
    const contentEl = this.shadowRoot.querySelector(".cardContent");
    const contentRect = contentEl
      ? contentEl.getBoundingClientRect()
      : cardRect;
    const scale = 2;
    const canvasW = Math.ceil(cardRect.width);
    let canvasH = Math.ceil(contentRect.bottom - cardRect.top) + 4; // up to content area

    const rel = (el) => {
      const r = el.getBoundingClientRect();
      return {
        x: Math.round(r.left - cardRect.left),
        y: Math.round(r.top - cardRect.top),
        w: Math.round(r.width),
        h: Math.round(r.height),
      };
    };

    // Pre-measure explanation text to ensure canvas is tall enough
    const explanationEl = this.shadowRoot.querySelector(".explanation");
    if (explanationEl?.textContent.trim()) {
      const p = rel(explanationEl);
      const fontSize = parseFloat(getComputedStyle(explanationEl).fontSize);
      const lineHeight = Math.round(fontSize * 1.5);
      const measureCanvas = document.createElement("canvas");
      const measureCtx = measureCanvas.getContext("2d");
      measureCtx.font = `${getComputedStyle(explanationEl).fontWeight} ${fontSize}px ${getComputedStyle(explanationEl).fontFamily}`;
      const textBottom = this._wrapText(measureCtx, explanationEl.textContent.trim(), p.x, p.y, p.w, lineHeight, { measureOnly: true });
      canvasH = Math.max(canvasH, textBottom + 6);
    }

    const canvas = document.createElement("canvas");
    canvas.width = canvasW * scale;
    canvas.height = canvasH * scale;
    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);

    const drawSvgEl = async (svgEl, x, y, maxW, maxH) => {
      const markup = new XMLSerializer().serializeToString(svgEl);
      const blob = new Blob([markup], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      try {
        const img = new Image();
        await new Promise((res, rej) => {
          img.onload = res;
          img.onerror = rej;
          img.src = url;
        });
        const natW = img.naturalWidth || maxW;
        const natH = img.naturalHeight || maxH;
        const drawW = natW * (maxH / natH);
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, y, maxW, maxH);
        ctx.clip();
        ctx.drawImage(img, x, y, drawW, maxH);
        ctx.restore();
      } finally {
        URL.revokeObjectURL(url);
      }
    };

    const cssVar = (name, fallback) => getComputedStyle(cardEl).getPropertyValue(name).trim() || fallback;

    const computedFont = (el) => {
      const s = getComputedStyle(el);
      return `${s.fontWeight} ${s.fontSize} ${s.fontFamily}`;
    };

    // ── Background ──
    ctx.fillStyle = cssVar("--bl-color-background", "#faf7f3");
    ctx.fillRect(0, 0, canvasW, canvasH);

    // ── Card border ──
    const borderColor = item.isNonPreferred
      ? cssVar("--bl-color-border-non-preferred", "#bc8f8f")
      : cssVar("--bl-color-border-card", "#d5d3cf");
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 1;
    this._drawRoundRect(ctx, 0.5, 0.5, canvasW - 1, canvasH - 1, 8);
    ctx.stroke();

    // ── Top bar separator ──
    const badgesEl = this.shadowRoot.querySelector(".cardBadges");
    if (badgesEl) {
      const bp = rel(badgesEl);
      ctx.strokeStyle = cssVar("--bl-color-border-card", "#d5d3cf");
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(bp.x + 8, bp.y + bp.h + 0.5);
      ctx.lineTo(bp.x + bp.w - 8, bp.y + bp.h + 0.5);
      ctx.stroke();
    }

    // ── "Character" badge ──
    const labelEl = this.shadowRoot.querySelector(".characterLabel");
    if (labelEl) {
      const p = rel(labelEl);
      ctx.fillStyle = cssVar("--bl-color-accent", "#b89530");
      this._drawRoundRect(ctx, p.x, p.y, p.w, p.h, 3);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = computedFont(labelEl);
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      ctx.fillText(labelEl.textContent.trim(), p.x + p.w / 2, p.y + p.h / 2);
      ctx.textAlign = "left";
    }

    // ── Gloss ──
    const glossEl = this.shadowRoot.querySelector(".resultGloss");
    if (glossEl) {
      const p = rel(glossEl);
      ctx.fillStyle = cssVar("--bl-color-text-primary", "#222");
      ctx.font = computedFont(glossEl);
      ctx.textBaseline = "middle";
      ctx.fillText(glossEl.textContent.trim(), p.x, p.y + p.h / 2, p.w);
    }

    // ── ID chip ──
    const idChipEl = this.shadowRoot.querySelector(".idChip");
    if (idChipEl) {
      const p = rel(idChipEl);
      ctx.fillStyle = cssVar("--bl-color-text-muted", "#888");
      ctx.font = computedFont(idChipEl);
      ctx.textBaseline = "middle";
      ctx.textAlign = "right";
      ctx.fillText(idChipEl.textContent.replace(/\s+/g, " ").trim(), p.x + p.w, p.y + p.h / 2);
      ctx.textAlign = "left";
    }

    // ── Main Bliss SVG ──
    const mainSvgEl = this.shadowRoot.querySelector(".resultSvg > svg");
    if (mainSvgEl) {
      const p = rel(mainSvgEl);
      const markup = new XMLSerializer().serializeToString(mainSvgEl);
      const blob = new Blob([markup], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      try {
        const img = new Image();
        await new Promise((res, rej) => {
          img.onload = res;
          img.onerror = rej;
          img.src = url;
        });
        ctx.save();
        this._drawRoundRect(ctx, p.x, p.y, p.w, p.h, 4);
        ctx.clip();
        ctx.drawImage(img, p.x, p.y, p.w, p.h);
        ctx.restore();
      } finally {
        URL.revokeObjectURL(url);
      }
    }

    // ── Additional language gloss (now in top bar) ──
    const addGlossEl = this.shadowRoot.querySelector(".topBarAddLang");
    if (addGlossEl) {
      const p = rel(addGlossEl);
      ctx.fillStyle = cssVar("--bl-color-text-secondary", "#555");
      ctx.font = computedFont(addGlossEl);
      ctx.textBaseline = "middle";
      ctx.fillText(addGlossEl.textContent.trim(), p.x, p.y + p.h / 2, p.w);
    }

    // ── Derivation parts ──
    for (const part of this.shadowRoot.querySelectorAll(".derivationPart")) {
      const pp = rel(part);
      ctx.strokeStyle = "rgba(0,0,0,0.08)";
      ctx.lineWidth = 1;
      this._drawRoundRect(ctx, pp.x + 0.5, pp.y + 0.5, pp.w - 1, pp.h - 1, 3);
      ctx.stroke();
      ctx.strokeStyle = "rgba(0,0,0,0.12)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(pp.x + 3, pp.y + pp.h);
      ctx.lineTo(pp.x + pp.w - 3, pp.y + pp.h);
      ctx.stroke();

      const miniSvg = part.querySelector(".derivedFromSvg > svg");
      if (miniSvg) {
        const sp = rel(miniSvg);
        await drawSvgEl(miniSvg, sp.x, sp.y, sp.w, sp.h);
      }

      const textEl = part.querySelector(".derivationPartText");
      if (textEl) {
        const tp = rel(textEl);
        ctx.fillStyle = "#333";
        ctx.font = computedFont(textEl);
        ctx.textBaseline = "middle";
        ctx.fillText(textEl.textContent.trim(), tp.x, tp.y + tp.h / 2, tp.w);
      }
    }

    // ── Explanation ──
    if (explanationEl?.textContent.trim()) {
      const p = rel(explanationEl);
      ctx.fillStyle = cssVar("--bl-color-text-secondary", "#555");
      ctx.font = computedFont(explanationEl);
      ctx.textBaseline = "top";
      const lineHeight = Math.round(parseFloat(getComputedStyle(explanationEl).fontSize) * 1.5);
      this._wrapText(ctx, explanationEl.textContent.trim(), p.x, p.y, p.w, lineHeight);
    }

    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  }

  async _handleCopyCard(e) {
    e.stopPropagation();
    const btn = e.currentTarget;
    const item = this.item;
    if (!item) return;

    try {
      const pngBlob = await this._renderCardPngBlob();
      if (!pngBlob) return;
      await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlob })]);
      this._showCopyFeedback(btn);
    } catch (err) {
      console.warn("Card copy failed:", err);
      const glossText = String(item.gloss ?? "").replace(/<[^>]*>/g, "");
      await navigator.clipboard.writeText(glossText).catch(() => {});
    }
  }

  async _handleDownloadCard(e) {
    e.stopPropagation();
    const item = this.item;
    if (!item) return;

    try {
      const pngBlob = await this._renderCardPngBlob();
      if (!pngBlob) return;
      const url = URL.createObjectURL(pngBlob);
      const a = document.createElement("a");
      a.href = url;
      const glossText =
        String(item.gloss ?? "entry").replace(/<[^>]*>/g, "").trim();
      a.download = `${glossText.replace(/[^a-z0-9]/gi, "_")}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn("Card download failed:", err);
    }
  }

  _drawRoundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }

  _wrapText(ctx, text, x, y, maxWidth, lineHeight, { measureOnly = false } = {}) {
    const words = text.split(" ");
    let line = "";
    let cy = y;
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        if (!measureOnly) ctx.fillText(line, x, cy);
        line = word;
        cy += lineHeight;
      } else {
        line = test;
      }
    }
    if (line) {
      if (!measureOnly) ctx.fillText(line, x, cy);
    }
    return cy + lineHeight;
  }

  _dispatchClipboard(e) {
    e.stopPropagation();
    const btn = e.currentTarget;
    this.dispatchEvent(
      new CustomEvent("clipboard-requested", {
        detail: {
          item: this.item,
          onSuccess: () => this._showCopyFeedback(btn),
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  _showCopyFeedback(btn) {
    if (!btn) return;
    const iconSpan = btn.querySelector(".icon");
    if (!iconSpan) return;
    const original = iconSpan.innerHTML;
    iconSpan.innerHTML = checkIcon;
    setTimeout(() => (iconSpan.innerHTML = original), 1500);
  }

  _getSubtypes(item) {
    const subtypes = [];
    if (item.isCombined) subtypes.push("Combined");
    if (item.isSuperimposed) subtypes.push("Superimposed");
    if (item.isSequenced) subtypes.push("Sequenced");
    if (item.isModified) subtypes.push("Modified");
    if (item.isPrimitive) subtypes.push("Primitive");
    return subtypes;
  }

  _renderExpandedInner(item) {
    const subtypes = this._getSubtypes(item);
    return html`
      ${item.isChar && subtypes.length > 0 ? html`<div>Type: ${subtypes.join(", ")}</div>` : ""}
      ${item.isNonPreferred ? html`<div class="nonPreferredNotice">BCI considers this entry non-preferred.</div>` : ""}
      ${item.isDualForm ? html`<div class="dualFormNotice">This grammatical marker is part of a group where indicator and corresponding modifier forms exist. <a href="/info/dual-form">Learn more</a></div>` : ""}
      <div class="expandActions">
        <span class="exportLabel">Bliss image (PNG/SVG):</span>
        <wa-button appearance="plain" size="small" @click="${this._dispatchDownload}">
          <span class="icon">${unsafeHTML(downloadIcon)}</span> Download
        </wa-button>
        <wa-button appearance="plain" size="small" @click="${this._dispatchClipboard}">
          <span class="icon">${unsafeHTML(clipboardIcon)}</span> To clipboard
        </wa-button>
      </div>
      <div class="expandActions">
        <span class="exportLabel">Entry image (PNG):</span>
        <wa-button appearance="plain" size="small" @click="${this._handleDownloadCard}">
          <span class="icon">${unsafeHTML(downloadIcon)}</span> Download
        </wa-button>
        <wa-button appearance="plain" size="small" @click="${this._handleCopyCard}">
          <span class="icon">${unsafeHTML(clipboardIcon)}</span> To clipboard
        </wa-button>
      </div>
    `;
  }

  _dispatchNavigate(e, id) {
    e.preventDefault();
    this.dispatchEvent(
      new CustomEvent("navigate-to-word", {
        detail: { id },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    const item = this.item;
    if (!item) return html``;

    return html`
      <wa-card
        class="${classMap({ "non-preferred": item.isNonPreferred })}"
        appearance="outlined"
      >
        <div class="cardBody">

          <!-- Top bar: character badge + gloss + ID (+ optional second-language row) -->
          <div class="cardBadges">
            ${item.isChar || item.isNonPreferred || item.isDualForm ? html`
              <span class="badgeGroup">
                ${item.isNonPreferred ? html`<span class="nonPreferredLabel">Non-Preferred</span>` : ""}
                ${item.isDualForm ? html`<span class="dualFormLabel">Dual Form</span>` : ""}
                ${item.isChar ? html`<span class="characterLabel">Character</span>` : ""}
              </span>
            ` : ""}
            <span class="resultGloss">${unsafeHTML(item.gloss)}</span>
            <span class="idChip">
              <span title="BCI Authorized Vocabulary ID">${item.bciAvId || "N/A"}</span>
              · <span title="Blissary ID${item.isChar ? " · Bliss SVG Builder input string" : ""}"
                >B${item.id}</span>
              ${item.isChar ? "" : html`· <span title="Bliss SVG Builder input string">${item.code}</span>`}
            </span>
            ${item.addLangGloss ? html`<span class="topBarAddLang">${unsafeHTML(item.addLangGloss)}</span>` : ""}
          </div>

          <!-- Content: SVG (left) + text column (right), independent heights -->
          <div class="cardContent">
            <a class="resultSvg" href="?q=B${item.id}"
              title=${ifDefined((this.searchString || "").trim().toLowerCase() === `b${item.id}` ? undefined : `Search B${item.id}`)}
              @click="${(e) => {
                e.preventDefault();
                this._dispatchShowMore();
              }}">
              ${this.getBlissSvg(item.code, item.pos)}
            </a>

            <div class="cardText">
              <div class="derivationParts">
                ${repeat(
                  item.derivationParts || [],
                  (dPart, index) => `${dPart.id}-${index}`,
                  (dPart) => html`
                    ${
                      dPart.type === "CODE"
                        ? html`
                        <a
                          href="?q=${dPart.id}"
                          title="Search ${dPart.id}"
                          @click="${(e) => (dPart.id ? this._dispatchNavigate(e, dPart.id) : e.preventDefault())}"
                        >
                          <div class="derivationPart">
                            <div class="derivedFromSvg">${this.getMiniBlissSvg(dPart.code)}</div>
                            <div class="derivationPartText">${(dPart.gloss || "").split(/(,|_\()/)[0]}</div>
                          </div>
                        </a>
                      `
                        : ""
                    }
                    ${
                      dPart.type === "POINTER"
                        ? html`
                        <div class="derivationPart derivationPartPointer">
                          <div class="derivedFromPointer">POINTER</div>
                        </div>
                      `
                        : ""
                    }
                    ${
                      dPart.type === "ARROW"
                        ? html`
                        <div class="derivationPart derivationPartArrow">
                          <div class="derivedFromArrow">ARROW</div>
                        </div>
                      `
                        : ""
                    }
                  `,
                )}
              </div>
              <div class="explanation">${item.explanation}</div>
            </div>

          </div>

          <!-- Expandable section: full-width, outside the SVG/text flex -->
          <div class="expandableSection ${this.expanded ? "open" : ""}">
            <div class="expandableContent">
              ${this._renderExpandedInner(item)}
            </div>
          </div>

          <!-- Chevron: floats over SVG bottom-center via negative margin, zero net height -->
          <div
            class="cardActions"
            role="button"
            tabindex="0"
            @click="${this._toggleExpanded}"
            @keydown="${(e) => e.key === "Enter" && this._toggleExpanded()}"
          >
            ${this.expanded ? unsafeHTML(chevronUpIcon) : unsafeHTML(chevronDownIcon)}
          </div>

        </div>
      </wa-card>
    `;
  }
}

customElements.define("bl-result-item", BlResultItem);
